import { v4 as uuid } from 'uuid';
import {
  Message,
  MessageEvent as ImMessageEvent,
  MessageFlag,
  ResponseMessage,
  StreamMessage
} from '../types/message';

const TIMEOUT = 30;

export interface SocketConfig {
  uid?: string;
  open_id?: string;
  user_id?: string;
  user_type?: string;
  phone_no?: string;
  source?: string;
  onConnectSuccess?: (params: { isRobot: boolean; isQueue: boolean; sessionId?: string }) => void;
  onConnectError?: () => void;
  testUrl?: string;
  channelid?: string;
  groupid?: string;
  sysnum?: string;
  from?: string;
  userName?: string;
  nick_name?: string;
  show_order?: string;
  jwt?: string;
  order_display_id?: string;
  order_type?: string;
  show_button_picture?: string;
  show_button_camera?: string;
  show_button_shoot?: string;
  link_mode?: string;
  city_id?: string;
  from_page_id?: string;
}

class Socket {
  private socket!: WebSocket;

  private readonly requestMap = new Map<
    number | string,
    { resolve?: (message: ResponseMessage) => void; reject?: (message: ResponseMessage) => void }
  >();

  private url: string;

  private heartBreat?: NodeJS.Timeout;

  private reconnectTimeout?: NodeJS.Timeout;

  private sendId = 0;

  private config: SocketConfig;

  private connecting = false;

  private sessionId?: string;

  closed = true;

  public onMessage?: (message: Message) => void;

  constructor(url: string, config: SocketConfig) {
    this.config = config;
    this.url = url;
    this.init();
  }

  private init(cb?: (success: boolean) => void, reConnect?: boolean) {
    this.connecting = true;
    this.socket = new WebSocket(this.url, this.config.jwt);
    this.socket.onopen = () => {
      this.connecting = false;
      if (cb) {
        cb(true);
      }
      this.closed = false;
      this.connect(reConnect);
    };
    this.socket.onmessage = event => {
      this.onReceive(event);
    };

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        clearTimeout(this.reconnectTimeout as NodeJS.Timeout);
        return;
      }
      if (
        (this.socket?.readyState === this.socket?.CLOSED ||
          this.socket?.readyState === this.socket?.CLOSING) &&
        !this.connecting
      ) {
        clearTimeout(this.reconnectTimeout as NodeJS.Timeout);
        this.connecting = true;
        this.closeHandle(cb);
      }
    });

    this.socket.onclose = () => {
      clearTimeout(this.reconnectTimeout as NodeJS.Timeout);
      this.connecting = true;
      this.closeHandle(cb);
    };
  }

  private closeHandle = (cb?: (success: boolean) => void) => {
    if (cb) {
      cb(false);
    }
    if (this.heartBreat || this.heartBreat === 0) {
      clearTimeout(this.heartBreat);
    }

    // 断开连接之后自动重新连接
    if (!this.closed || !this.connecting) {
      this.reconnect();
    }
  };

  private reconnect() {
    this.init(success => {
      clearTimeout(this.reconnectTimeout as NodeJS.Timeout);
      this.reconnectTimeout = setTimeout(() => {
        if (success) {
          if (this.reconnectTimeout || this.reconnectTimeout === 0) {
            clearTimeout(this.reconnectTimeout);
          }
        } else {
          this.reconnect();
        }
      }, 30 * 1000);
    }, true);
  }

  private async online(reOnline?: boolean) {
    const {
      open_id,
      groupid,
      source,
      channelid,
      user_type,
      nick_name,
      userName,
      user_id,
      order_display_id,
      city_id
    } = this.config;
    return this.request({
      module: 'im',
      event: ImMessageEvent.ONLINE,
      data: {
        orderId: order_display_id,
        cityId: city_id,
        urlData: {
          groupId: groupid,
          openId: open_id,
          source: Number(source),
          channelId: Number(channelid),
          identity: Number(user_type),
          nickName: userName || nick_name,
          userId: user_id
        },
        reOnline: reOnline ? 1 : 0
      }
    });
  }

  private async connect(reConnect?: boolean) {
    try {
      await this.request({
        module: 'common',
        event: ImMessageEvent.REGISTER,
        data: { jwt: this.config.jwt || '' }
      });
      const res = await this.online(reConnect);

      const { sessionId, serviceType, isQueue } = res.data || {};
      this.sessionId = sessionId;
      if (this.config.onConnectSuccess) {
        this.config.onConnectSuccess({
          isRobot: serviceType !== 1,
          isQueue: isQueue === 1,
          sessionId
        });
      }

      this.refreshHeartBreat();
    } catch (err) {
      if (this.config.onConnectError) {
        this.config.onConnectError();
      }
    }
  }

  private refreshHeartBreat() {
    if (this.heartBreat || this.heartBreat === 0) {
      clearTimeout(this.heartBreat);
    }
    this.heartBreat = setTimeout(() => {
      this.send({ module: 'common', event: ImMessageEvent.PING, data: null });
      this.refreshHeartBreat();
    }, TIMEOUT * 1000);
  }

  private onReceive(event: MessageEvent) {
    const message: ResponseMessage = JSON.parse(event.data);
    if (message.type === 2 && (message.id || message.id === 0) && this.requestMap.has(message.id)) {
      const promise = this.requestMap.get(message.id);
      this.requestMap.delete(message.id);
      if (promise) {
        const { data } = message;
        if (data.ret === 0) {
          if (promise.resolve) {
            return promise.resolve(message);
          }
        }
        if (promise.reject) {
          return promise.reject(message);
        }
      }
    } else if (message.type === 3) {
      if (this.onMessage) {
        if (!message.sessionId && this.sessionId) {
          message.sessionId = this.sessionId;
        }
        this.onMessage(message as Message);
      }
      if (
        message.event === ImMessageEvent.MESSAGE &&
        message.data.to === this.config.uid &&
        message.data.isRead !== 2
      ) {
        if (message.data.msgId) {
          if (message.data.type === 'stream') {
            const { data } = (message as unknown) as StreamMessage;
            if (['start', 'first', 'middle'].includes(data.content?.streamType as string)) {
              return;
            }
          }
          this.sendAck([message.data.msgId]);
        }
      }
      if (
        message.event === ImMessageEvent.MESSAGE &&
        message.data.flag === MessageFlag.SESSION_CLOSED_MSG
      ) {
        try {
          this.online(true);
        } catch (err) {
          console.log(err);
        }
      }
    }
  }

  sendAck(msgIds: string[]) {
    this.sendMessage({ event: ImMessageEvent.ACK, data: { msgIds } });
  }

  private sendMessage(message: string | Partial<Message>): Message {
    const baseMessage = this.generateBaseMessage();
    const contactInfo =
      typeof message === 'string' || message.event === ImMessageEvent.MESSAGE
        ? {
            from: this.config.uid,
            to: 1000
          }
        : {};
    const item =
      typeof message === 'string'
        ? {
            ...baseMessage,
            event: ImMessageEvent.MESSAGE,
            data: { ...contactInfo, type: 'text', content: message.trim(), cmId: uuid() }
          }
        : {
            ...baseMessage,
            ...message,

            data:
              message.event === ImMessageEvent.MESSAGE || message.event === ImMessageEvent.RATING
                ? { ...contactInfo, ...message.data, cmId: message.data?.cmId || uuid() }
                : message.data
          };
    if (this.socket.readyState !== 1) {
      return item as Message;
    }
    this.socket.send(JSON.stringify(item));
    this.refreshHeartBreat();

    return item as Message;
  }

  generateBaseMessage() {
    const baseMessage = {
      id: this.sendId,
      type: 1,
      uid: this.config.uid,
      module: 'im',
      timestamp: Date.now(),
      tenant: 'hll',
      channelId: this.config.channelid
    };
    this.sendId += 1;

    return baseMessage;
  }

  async request(message: Partial<Message>): Promise<ResponseMessage> {
    const id = this.sendId;
    this.sendMessage(message);
    const promise = new Promise<ResponseMessage>((resolve, reject) => {
      this.requestMap.set(id, { resolve, reject });
    });

    return promise;
  }

  send(
    message: string | Partial<Message>,
    options?: {
      onSuccess?(message: ResponseMessage): void;
      onError?(message: ResponseMessage): void;
    }
  ) {
    const id = this.sendId;

    let timeout: NodeJS.Timeout;
    if (options) {
      if (options.onError) {
        timeout = setTimeout(() => {
          if (this.requestMap.has(id)) {
            const request = this.requestMap.get(id);
            this.requestMap.delete(id);

            if (request && request.reject) {
              request.reject({
                id,
                event: typeof message === 'string' ? ImMessageEvent.MESSAGE : message.event,
                type: 2,
                data: { ret: -1, msg: '请求超时' }
              } as ResponseMessage);
            }
          }
        }, 60 * 1000);
      }

      this.requestMap.set(id, {
        resolve: msg => {
          clearTimeout(timeout);
          if (options.onSuccess) {
            options.onSuccess(msg);
          }
        },
        reject: err => {
          clearTimeout(timeout);
          if (options.onError) {
            options.onError(err);
          }
        }
      });
    }
    return this.sendMessage(message);
  }

  disconnect() {
    this.closed = true;
    this.socket.close();

    if (this.reconnectTimeout || this.reconnectTimeout === 0) {
      clearTimeout(this.reconnectTimeout);
    }
    if (this.heartBreat || this.heartBreat === 0) {
      clearTimeout(this.heartBreat);
    }
    this.requestMap.forEach((promise, key) => {
      if (promise.reject) {
        promise.reject({
          id: key,
          type: 2,
          event: ImMessageEvent.MESSAGE,
          data: { type: null, ret: 1024, msg: '连接已断开' }
        });
      }
    });
    this.requestMap.clear();
  }

  close() {
    this.send({ event: ImMessageEvent.OFFLINE, data: null });
    this.send({ event: ImMessageEvent.CLOSE, data: null });
    this.disconnect();
  }

  async closeAsync() {
    try {
      await Promise.all([
        this.request({ event: ImMessageEvent.OFFLINE, data: null }),
        this.request({ event: ImMessageEvent.CLOSE, data: null })
      ]);
      this.disconnect();
    } catch (err) {
      return err;
    }
  }
}

export default Socket;
